<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiTooltip, UiStandardButton } from "@shopexperts/shared";
import {
  useCurrentExpertOrgId,
  useExpertWallet,
  useWalletPreviewCollapsed,
} from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import ExpertWalletSidebarPreviewLevelBar from "./ExpertWalletSidebarPreviewLevelBar.vue";

const props = withDefaults(
  defineProps<{
    /** Visual density. `mobile` is roomier for the slide-out menu. */
    variant?: "sidebar" | "mobile";
  }>(),
  {
    variant: "sidebar",
  },
);

const { orgId } = useCurrentExpertOrgId();
const { collapsed, toggle } = useWalletPreviewCollapsed();

const {
  availableBalanceCents,
  holdBalanceCents,
  totalGrossSalesCents,
  currency,
  isLoading,
  error,
} = useExpertWallet({ actingOrgId: orgId });

const hasData = computed(() => orgId.value != null && !error.value);

const availableLabel = computed(() =>
  hasData.value ? formatCurrency(availableBalanceCents.value, currency.value) : "—",
);
const heldLabel = computed(() =>
  hasData.value ? formatCurrency(holdBalanceCents.value, currency.value) : "—",
);
const grossLabel = computed(() =>
  hasData.value ? formatCurrency(totalGrossSalesCents.value, currency.value) : "—",
);

const showSkeleton = computed(() => isLoading.value && hasData.value);

const isMobile = computed(() => props.variant === "mobile");

const regionId = `expert-wallet-preview-${Math.random().toString(36).slice(2)}`;
</script>

<template>
  <section class="font-archivo flex flex-col" :class="isMobile ? 'gap-4' : 'gap-3'">
    <!-- Wallet group: header, balances, and CTA stay tightly related -->
    <div class="flex flex-col" :class="isMobile ? 'gap-3' : 'gap-2'">
    <!-- Header doubles as the expand / collapse control -->
    <button
      type="button"
      class="group flex w-full cursor-pointer items-center gap-1 text-left"
      :aria-expanded="!collapsed"
      :aria-controls="regionId"
      @click="toggle"
    >
      <UiIcon name="wallet" class="h-3.5 w-3.5 shrink-0 text-se-gray-500" />
      <UiText
        as="span"
        variant="override"
        text="Shopexperts Wallet"
        class="truncate font-semibold uppercase tracking-[0.1em] text-se-gray-500"
        :class="isMobile ? 'text-[11px]' : 'text-[10px]'"
      />
      <UiIcon
        name="chevron-down"
        class="h-3 w-3 shrink-0 text-se-gray-500 transition-transform duration-300 group-hover:text-se-gray-700"
        :class="collapsed ? '-rotate-90' : 'rotate-0'"
      />
    </button>

    <!-- Available balance hero (always visible) -->
    <div class="flex flex-col gap-1">
      <UiText
        as="p"
        variant="override"
        text="Available balance"
        class="text-se-dark"
        :class="isMobile ? 'text-h5' : 'text-h6'"
      />
      <div v-if="showSkeleton" class="h-8 w-32 animate-pulse rounded bg-se-gray-200" />
      <UiText
        v-else
        as="p"
        variant="override"
        :text="availableLabel"
        class="font-bold leading-tight tracking-tight text-se-dark"
        :class="isMobile ? 'text-h1' : 'text-h2'"
      />
    </div>

    <!-- Collapsible details: held balance + total gross sales only -->
    <div
      :id="regionId"
      class="grid transition-[grid-template-rows] duration-300 ease-out"
      :class="collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'"
    >
      <div class="min-h-0 overflow-hidden">
        <!-- Secondary balances as slim rows -->
        <dl class="flex flex-col" :class="isMobile ? 'gap-2' : 'gap-1.5'">
          <div class="flex items-center justify-between gap-3">
            <dt class="flex min-w-0 items-center gap-1">
              <UiText
                as="span"
                variant="override"
                text="Held balance"
                class="text-se-dark"
                :class="isMobile ? 'text-h5' : 'text-h6'"
              />
              <UiTooltip
                content="Funds reserved from in-progress work. They move to your available balance when the project is completed or an admin releases them."
              >
                <UiIcon name="info-circle" class="h-3 w-3 shrink-0 text-se-gray-500" />
              </UiTooltip>
            </dt>
            <dd v-if="showSkeleton" class="h-4 w-16 animate-pulse rounded bg-se-gray-200" />
            <dd v-else class="shrink-0">
              <UiText
                as="span"
                variant="override"
                :text="heldLabel"
                class="font-semibold tabular-nums text-se-dark"
                :class="isMobile ? 'text-h4' : 'text-h5'"
              />
            </dd>
          </div>

          <div class="flex items-center justify-between gap-3">
            <dt class="flex min-w-0 items-center gap-1">
              <UiText
                as="span"
                variant="override"
                text="Total gross sales"
                class="text-se-dark"
                :class="isMobile ? 'text-h5' : 'text-h6'"
              />
              <UiTooltip
                content="Your lifetime gross sales across all completed work. This is what counts toward your expert level."
              >
                <UiIcon name="info-circle" class="h-3 w-3 shrink-0 text-se-gray-500" />
              </UiTooltip>
            </dt>
            <dd v-if="showSkeleton" class="h-4 w-20 animate-pulse rounded bg-se-gray-200" />
            <dd v-else class="shrink-0">
              <UiText
                as="span"
                variant="override"
                :text="grossLabel"
                class="font-semibold tabular-nums text-se-dark"
                :class="isMobile ? 'text-h4' : 'text-h5'"
              />
            </dd>
          </div>
        </dl>
      </div>
    </div>

    <!-- CTA into the full wallet page (transactions + details) -->
    <UiStandardButton
      href="/expert/wallet"
      variant="outline"
      size="xs"
      justify="center"
      label="View wallet"
      class="w-full !rounded !border-se-gray-700"
    />
    </div>

    <!-- Level group: roomier, symmetric gap around the separator sets it apart -->
    <div class="flex flex-col" :class="isMobile ? 'gap-4' : 'gap-3'">
      <div class="h-px w-full bg-se-gray-200" />
      <ExpertWalletSidebarPreviewLevelBar :acting-org-id="orgId" :variant="variant" />
    </div>
  </section>
</template>
